import { detectBgColors } from '~/features/background/detect';
import { sha256Hex } from '~/lib/sha256';
import { resetSession, useStore } from '~/store';
import type { Rect } from '~/types/pipeline';

function rectFitsImage(r: Rect | null, w: number, h: number): r is Rect {
  return !!r && r.x >= 0 && r.y >= 0 && r.w > 0 && r.h > 0 && r.x + r.w <= w && r.y + r.h <= h;
}

function dividersFit(xs: number[] | null, lo: number, hi: number): boolean {
  if (!xs || xs.length < 2) return false;
  const first = xs[0];
  const last = xs.at(-1);
  if (first === undefined || last === undefined) return false;
  if (first < lo || last > hi) return false;
  for (let i = 1; i < xs.length; i++) {
    const prev = xs[i - 1];
    const cur = xs[i];
    if (prev === undefined || cur === undefined || cur <= prev) return false;
  }
  return true;
}

function validateCropAndDividers(imageData: ImageData): void {
  const s = useStore.getState();
  const { width: w, height: h } = imageData;
  const crop = s.grid.cropRect;
  if (!rectFitsImage(crop, w, h)) {
    s.setCropRect({ x: 0, y: 0, w, h });
    s.clearDividers();
    return;
  }
  if (
    !dividersFit(s.grid.colDividers, crop.x, crop.x + crop.w) ||
    !dividersFit(s.grid.rowDividers, crop.y, crop.y + crop.h)
  ) {
    s.clearDividers();
  }
}

/**
 * Apply a freshly-loaded source image to the store.
 *
 * Same image (hash match): restores runtime fields only; persisted settings
 * (bgColors, sliders, crop) are preserved verbatim.
 *
 * Different image: resets the entire session, sets the new source, auto-detects
 * background colors, and clears undo history.
 */
export async function loadSource(input: {
  fileName: string;
  bytes: ArrayBuffer;
  imageData: ImageData;
}): Promise<void> {
  const hash = await sha256Hex(input.bytes);
  const s = useStore.getState();

  if (hash === s.source.hash) {
    s.setSource({ fileName: input.fileName, imageData: input.imageData, hash });
    validateCropAndDividers(input.imageData);
    return;
  }

  resetSession();
  const ns = useStore.getState();
  ns.setSource({ fileName: input.fileName, imageData: input.imageData, hash });
  ns.setBgColors(detectBgColors(input.imageData));
  validateCropAndDividers(input.imageData);
  useStore.temporal.getState().clear();
}
